/**
 * S-25 Own profile & edit (V1 auth-profiles-gdpr) — /me.
 *
 * View-as-visitor toggle, photo manager (6 slots + video + voice prompt),
 * bio / anthem / Opening Move editors, prompts, vitals, interests (incl. the
 * "red flags I'm proud of" set), gender/pronouns/show_me (EC-17 30-day rule
 * surfaced via ©profile.gender_change_note), Profile Power Level meter (#13)
 * with next-best-action chips, verification chip, pause, data export (S-29)
 * and the delete-account entry (S-30). Discovery-hold banner per EC-37.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GENDERS,
  RELATIONSHIP_INTENTS,
  copy,
  formatCopy,
  resolveCopy,
  type Gender,
} from "@humble/shared";
import {
  Badge,
  Button,
  Card,
  Mascot,
  Skeleton,
  springs,
  VerifiedBadge,
  toast,
} from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { DeleteAccountSheet } from "../account/delete-sheet.js";
import { AuthedImage } from "./authed-image.js";
import { ExportSheet } from "./export-sheet.js";
import { uploadMultipart } from "./media-api.js";
import { PhotoManager, type PhotoItem } from "./photo-manager.js";
import { PromptsEditor, type PromptAnswer } from "./prompts-editor.js";
import { VitalsEditor, type VitalsMap } from "./vitals-editor.js";
import "./profile.css";

interface MeWire {
  user?: { id?: string; display_name?: string | null; verified?: boolean };
  email?: string;
  lifecycle?: string;
  completeness?: number;
  premium?: boolean;
  discovery_hold?: boolean;
  restrictions?: Array<{ surface?: string; until?: string }>;
  profile?: {
    gender?: Gender;
    pronouns?: string;
    bio?: string;
    anthem?: string;
    opening_move?: string;
    intent?: string;
    interests?: string[];
    vitals?: VitalsMap;
    prompts?: Array<{ prompt_id?: number; answer?: string }>;
    photos?: string[];
    photo_items?: Array<{ id?: string; status?: string; position?: number | null }>;
    media_items?: Array<{ id?: string; kind?: string; status?: string }>;
  };
}

interface InterestTag {
  id: number;
  label: string;
  red_flag: boolean;
}

export function MeScreen() {
  const [me, setMe] = useState<MeWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [visitorView, setVisitorView] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const view = (await api.get("/me")) as MeWire;
      setMe(view);
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading || !me) {
    return (
      <div className="me" data-screen="S-25">
        <div style={{ display: "flex", justifyContent: "center", padding: "18px 0 6px" }}>
          <Mascot mood="loading" />
        </div>
        <Skeleton height={60} radius={16} />
        <Skeleton height={220} radius={20} />
        <Skeleton height={160} radius={20} />
      </div>
    );
  }

  const profile = me.profile ?? {};
  const photos: PhotoItem[] = (profile.photo_items ?? [])
    .filter((p): p is { id: string; status: string; position: number | null } => typeof p.id === "string")
    .map((p) => ({ id: p.id, kind: "photo", status: p.status, position: p.position ?? null }));
  const prompts: PromptAnswer[] = (profile.prompts ?? [])
    .filter((p): p is { prompt_id: number; answer: string } => typeof p.prompt_id === "number")
    .map((p) => ({ prompt_id: p.prompt_id, answer: p.answer ?? "" }));
  const voice = (profile.media_items ?? []).find((m) => m.kind === "voice_prompt");
  const video = (profile.media_items ?? []).find((m) => m.kind === "profile_video");
  const paused = me.lifecycle === "paused";
  const completeness = me.completeness ?? 0;

  return (
    <div className="me" data-screen="S-25">
      {me.discovery_hold && (
        <div className="banner" role="alert">
          {copy.moderation.photo_hold}
        </div>
      )}
      {paused && (
        <div className="banner" role="status">
          {copy.pause.banner.replace("— [Unpause]", "")}
          <Button size="sm" variant="primary" style={{ marginLeft: 8 }} onClick={() => void togglePause()}>
            Unpause
          </Button>
        </div>
      )}
      {(me.restrictions ?? []).map((r) => (
        <div className="banner" role="alert" key={`${r.surface}-${r.until}`}>
          {formatCopy(copy.safety.restricted, {
            surface: r.surface ?? "a feature",
            date: r.until ? new Date(r.until).toLocaleString() : "review",
          })}
        </div>
      ))}

      <header className="me__head">
        <div>
          <h1 className="me__name">
            {me.user?.display_name ?? "Your profile"}{" "}
            {me.user?.verified ? <VerifiedBadge /> : <Badge>unverified</Badge>}
          </h1>
          <p className="type-editorial me__lede">{resolveCopy("profile.lede")}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setVisitorView((v) => !v)}>
          {visitorView ? "Back to editing" : "View as visitor"}
        </Button>
      </header>

      <section className="power" aria-label="Profile Power Level">
        <span className="power__label">
          {formatCopy(copy.profile.power_level, { n: completeness })}
        </span>
        <div className="power__meter">
          <motion.div
            className="power__fill"
            initial={{ width: 0 }}
            animate={{ width: `${completeness}%` }}
            transition={springs.gentle}
          />
        </div>
        {!visitorView && (
          <div className="power__chips">
            {photos.length < 4 && <Badge>+ photo = power</Badge>}
            {prompts.length < 3 && <Badge>answer a prompt</Badge>}
            {!voice && <Badge>add your voice</Badge>}
            {!me.user?.verified && <Badge>get verified (+15)</Badge>}
          </div>
        )}
      </section>

      {visitorView ? (
        <VisitorPreview me={me} />
      ) : (
        <>
          <Card>
            <h2 className="me__section-title">Photos</h2>
            {photos.length <= 1 && <p className="text-faint">{copy.profile.empty_slots.photos}</p>}
            <PhotoManager photos={photos} onChanged={() => void refresh()} />
            <SingletonMedia
              label="Voice prompt"
              nudge={copy.profile.empty_slots.voice}
              accept="audio/*"
              endpoint="/me/voice-prompt"
              item={voice}
              onChanged={() => void refresh()}
            />
            <SingletonMedia
              label="Profile video (muted, ≤20 s)"
              accept="video/*"
              endpoint="/me/video"
              item={video}
              onChanged={() => void refresh()}
            />
          </Card>

          <Card>
            <h2 className="me__section-title">Bio, anthem & opening move</h2>
            <TextFieldsEditor
              bio={profile.bio ?? ""}
              anthem={profile.anthem ?? ""}
              openingMove={profile.opening_move ?? ""}
              intent={profile.intent ?? ""}
              onSaved={() => void refresh()}
            />
          </Card>

          <Card>
            <h2 className="me__section-title">Prompts</h2>
            <PromptsEditor answers={prompts} onSaved={() => void refresh()} />
          </Card>

          <Card>
            <h2 className="me__section-title">Vitals — all optional</h2>
            <VitalsEditor vitals={profile.vitals ?? {}} onSaved={() => void refresh()} />
          </Card>

          <Card>
            <h2 className="me__section-title">Interests & proud red flags</h2>
            <InterestsEditor current={profile.interests ?? []} onSaved={() => void refresh()} />
          </Card>

          <Card>
            <h2 className="me__section-title">Identity</h2>
            <IdentityEditor
              gender={profile.gender}
              pronouns={profile.pronouns ?? ""}
              onSaved={() => void refresh()}
            />
          </Card>

          <Card>
            <h2 className="me__section-title">Account</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {/* S-27 › Game features — per-user mechanic toggles (0002) */}
              <Link to="/settings/features" className="btn btn--ghost">
                {resolveCopy("features.settings_cta") ?? "Game features settings"}
              </Link>
              {!paused && (
                <Button variant="ghost" onClick={() => void togglePause()}>
                  Pause my account
                </Button>
              )}
              <Button variant="ghost" onClick={() => setExportOpen(true)}>
                Request my data (GDPR export)
              </Button>
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                Delete my account
              </Button>
            </div>
          </Card>
        </>
      )}

      <DeleteAccountSheet open={deleteOpen} onClose={() => setDeleteOpen(false)} lifecycle={me.lifecycle} />
      <ExportSheet open={exportOpen} onClose={() => setExportOpen(false)} />
    </div>
  );

  async function togglePause() {
    try {
      if (paused) await api.post("/me/unpause");
      else await api.post("/me/pause");
      await refresh();
      toast(paused ? "Unpaused. You exist again." : copy.pause.explainer, "info");
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }
}

// ---------------------------------------------------------------------------
// view-as-visitor (S-25 toggle — renders the public shape of the same data)
// ---------------------------------------------------------------------------
function VisitorPreview({ me }: { me: MeWire }) {
  const profile = me.profile ?? {};
  const livePhotos = (profile.photo_items ?? []).filter((p) => p.status === "live");
  return (
    <Card data-testid="visitor-preview" className="me__visitor">
      {livePhotos[0]?.id ? (
        <AuthedImage mediaId={livePhotos[0].id} alt="Your first photo" />
      ) : (
        <p className="text-faint">No live photo yet — visitors see nothing until one passes review.</p>
      )}
      <h2 className="me__name" style={{ margin: "10px 0 2px", fontSize: "1.4rem" }}>
        {me.user?.display_name} {me.user?.verified && <VerifiedBadge />}
      </h2>
      {profile.pronouns && <p className="text-faint">{profile.pronouns}</p>}
      {profile.bio && <p>{profile.bio}</p>}
      {profile.anthem && <p className="me__anthem">♪ {profile.anthem}</p>}
      {(profile.interests ?? []).length > 0 && (
        <div className="chips">
          {(profile.interests ?? []).map((label) => (
            <span key={label} className="chip-toggle chip-toggle--on">
              {label}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// bio / anthem / opening move / intent
// ---------------------------------------------------------------------------
function TextFieldsEditor(props: {
  bio: string;
  anthem: string;
  openingMove: string;
  intent: string;
  onSaved: () => void;
}) {
  const [bio, setBio] = useState(props.bio);
  const [anthem, setAnthem] = useState(props.anthem);
  const [openingMove, setOpeningMove] = useState(props.openingMove);
  const [intent, setIntent] = useState(props.intent);
  const [busy, setBusy] = useState(false);
  const dirty =
    bio !== props.bio || anthem !== props.anthem || openingMove !== props.openingMove || intent !== props.intent;

  async function onSave() {
    if (busy || !dirty) return;
    setBusy(true);
    try {
      await api.patch("/me/profile", {
        body: {
          bio: bio || null,
          anthem: anthem || null,
          opening_move: openingMove || null,
          intent: intent || null,
        } as never,
      });
      toast("Saved.", "success");
      props.onSaved();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="field">
        <label htmlFor="me-bio">Bio</label>
        <textarea
          id="me-bio"
          rows={3}
          maxLength={500}
          placeholder={copy.profile.bio_placeholder}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="me-anthem">Anthem (one line)</label>
        <input
          id="me-anthem"
          type="text"
          maxLength={60}
          placeholder={copy.profile.anthem_placeholder}
          value={anthem}
          onChange={(e) => setAnthem(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="me-opening-move">Opening Move</label>
        <input
          id="me-opening-move"
          type="text"
          maxLength={140}
          placeholder={copy.profile.opening_move}
          value={openingMove}
          onChange={(e) => setOpeningMove(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="me-intent">Looking for</label>
        <select id="me-intent" value={intent} onChange={(e) => setIntent(e.target.value)}>
          <option value="">—</option>
          {RELATIONSHIP_INTENTS.map((i) => (
            <option key={i} value={i}>
              {i.replaceAll("_", " ")}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button size="sm" variant="primary" loading={busy} disabled={!dirty} onClick={() => void onSave()}>
          Save
        </Button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// interests (≤8, incl. red-flag set — PRD #6)
// ---------------------------------------------------------------------------
function InterestsEditor({ current, onSaved }: { current: string[]; onSaved: () => void }) {
  const [catalog, setCatalog] = useState<InterestTag[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(current));
  const [busy, setBusy] = useState(false);
  const dirty = useMemo(() => {
    const a = [...selected].sort().join("|");
    const b = [...current].sort().join("|");
    return a !== b;
  }, [selected, current]);

  useEffect(() => {
    void api
      .get("/interests")
      .then((list) => {
        if (Array.isArray(list)) {
          setCatalog(
            list.filter(
              (t): t is InterestTag => typeof t.id === "number" && typeof t.label === "string",
            ),
          );
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => setSelected(new Set(current)), [current]);

  function toggle(label: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else if (next.size < 8) next.add(label);
      else toast("Eight interests max. Curate your personality.", "announcer");
      return next;
    });
  }

  async function onSave() {
    if (busy || !dirty) return;
    const ids = catalog.filter((t) => selected.has(t.label)).map((t) => t.id);
    setBusy(true);
    try {
      await api.patch("/me/profile", { body: { interest_tag_ids: ids } as never });
      toast("Interests saved.", "success");
      onSaved();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  }

  const plain = catalog.filter((t) => !t.red_flag);
  const flags = catalog.filter((t) => t.red_flag);
  return (
    <>
      <div className="chips">
        {plain.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`chip-toggle${selected.has(t.label) ? " chip-toggle--on" : ""}`}
            onClick={() => toggle(t.label)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {flags.length > 0 && (
        <>
          <p className="text-faint" style={{ margin: "8px 0 4px", fontSize: "0.85rem" }}>
            Red flags I'm proud of
          </p>
          <div className="chips">
            {flags.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`chip-toggle chip-toggle--flag${selected.has(t.label) ? " chip-toggle--on" : ""}`}
                onClick={() => toggle(t.label)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <Button size="sm" variant="primary" loading={busy} disabled={!dirty} onClick={() => void onSave()}>
          Save interests
        </Button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// identity editor (gender 1×/30 d — EC-17; show_me set fresh on change)
// ---------------------------------------------------------------------------
function IdentityEditor(props: { gender?: Gender; pronouns: string; onSaved: () => void }) {
  const [gender, setGender] = useState<Gender | "">(props.gender ?? "");
  const [pronouns, setPronouns] = useState(props.pronouns);
  const [showMe, setShowMe] = useState<Set<Gender>>(new Set());
  const [busy, setBusy] = useState(false);

  const genderChanged = gender !== "" && gender !== props.gender;
  const dirty = genderChanged || pronouns !== props.pronouns || showMe.size > 0;

  async function onSave() {
    if (busy || !dirty) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = { pronouns: pronouns || null };
      if (genderChanged) body.gender = gender;
      if (showMe.size > 0) body.show_me = [...showMe];
      await api.patch("/me/profile", { body: body as never });
      toast("Identity saved.", "success");
      setShowMe(new Set());
      props.onSaved();
    } catch (err) {
      if (isApiError(err, "GENDER_EDIT_COOLDOWN")) {
        toast(copy.profile.gender_change_note, "error");
      } else if (isApiError(err)) {
        toast(err.message, "error");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="field">
        <label htmlFor="me-gender">Gender</label>
        <select id="me-gender" value={gender} onChange={(e) => setGender(e.target.value as Gender)}>
          {GENDERS.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </div>
      <p className="text-faint" style={{ fontSize: "0.85rem" }}>
        {copy.profile.gender_change_note}
      </p>
      <div className="field">
        <label htmlFor="me-pronouns">Pronouns</label>
        <input
          id="me-pronouns"
          type="text"
          maxLength={30}
          value={pronouns}
          onChange={(e) => setPronouns(e.target.value)}
        />
      </div>
      <p className="text-faint" style={{ fontSize: "0.85rem", marginBottom: 4 }}>
        Show me (leave untouched to keep your current setting):
      </p>
      <div className="chips">
        {GENDERS.map((g) => (
          <button
            key={g}
            type="button"
            className={`chip-toggle${showMe.has(g) ? " chip-toggle--on" : ""}`}
            onClick={() =>
              setShowMe((prev) => {
                const next = new Set(prev);
                if (next.has(g)) next.delete(g);
                else next.add(g);
                return next;
              })
            }
          >
            {g === "woman" ? "women" : g === "man" ? "men" : "nonbinary people"}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <Button size="sm" variant="primary" loading={busy} disabled={!dirty} onClick={() => void onSave()}>
          Save identity
        </Button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// singleton media (voice prompt / profile video)
// ---------------------------------------------------------------------------
function SingletonMedia(props: {
  label: string;
  nudge?: string;
  accept: string;
  endpoint: "/me/voice-prompt" | "/me/video";
  item?: { id?: string; status?: string };
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const inputId = `singleton-${props.endpoint.replaceAll("/", "-")}`;

  async function onUpload(file: File | undefined) {
    if (!file || busy) return;
    setBusy(true);
    try {
      await uploadMultipart(props.endpoint, file);
      toast(copy.moderation.photo_pending.replace("photo", "upload"), "info");
      props.onChanged();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    if (busy) return;
    setBusy(true);
    try {
      if (props.endpoint === "/me/voice-prompt") await api.delete("/me/voice-prompt");
      else await api.delete("/me/video");
      props.onChanged();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 10 }}>
      <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: "0.9rem" }}>{props.label}</p>
      {props.item?.id ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="text-dim" style={{ fontSize: "0.85rem" }}>
            {props.item.status === "live" ? "Live" : "In review"}
          </span>
          <Button size="sm" variant="danger" loading={busy} onClick={() => void onRemove()}>
            Remove
          </Button>
        </div>
      ) : (
        <>
          {props.nudge && (
            <p className="text-faint" style={{ margin: "0 0 4px", fontSize: "0.85rem" }}>
              {props.nudge}
            </p>
          )}
          <input
            id={inputId}
            type="file"
            accept={props.accept}
            disabled={busy}
            onChange={(e) => {
              void onUpload(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </>
      )}
    </div>
  );
}
